import { useEffect, useRef } from 'react';

export interface SignaturePadHandle {
  clear: () => void;
  isEmpty: () => boolean;
  toDataUrl: () => string | null;
}

interface SignaturePadProps {
  onChange?: (empty: boolean) => void;
  className?: string;
  handleRef?: React.MutableRefObject<SignaturePadHandle | null>;
}

/**
 * Finger/stylus signature pad for the referee tablet. Mirrors the web
 * app's component (apps/web/src/components/SignaturePad.tsx) — kept as
 * a local copy because the referee PWA is a standalone Vite bundle and
 * doesn't import from apps/web.
 */
export function SignaturePad({ onChange, className, handleRef }: SignaturePadProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawingRef = useRef(false);
  const emptyRef = useRef(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#111';

    const pos = (e: PointerEvent) => {
      const r = canvas.getBoundingClientRect();
      return { x: e.clientX - r.left, y: e.clientY - r.top };
    };

    const down = (e: PointerEvent) => {
      drawingRef.current = true;
      const { x, y } = pos(e);
      ctx.beginPath();
      ctx.moveTo(x, y);
      canvas.setPointerCapture(e.pointerId);
    };
    const move = (e: PointerEvent) => {
      if (!drawingRef.current) return;
      const { x, y } = pos(e);
      ctx.lineTo(x, y);
      ctx.stroke();
      if (emptyRef.current) {
        emptyRef.current = false;
        onChange?.(false);
      }
    };
    const up = () => {
      drawingRef.current = false;
    };

    canvas.addEventListener('pointerdown', down);
    canvas.addEventListener('pointermove', move);
    canvas.addEventListener('pointerup', up);
    canvas.addEventListener('pointercancel', up);

    if (handleRef) {
      handleRef.current = {
        clear: () => {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          emptyRef.current = true;
          onChange?.(true);
        },
        isEmpty: () => emptyRef.current,
        toDataUrl: () => (emptyRef.current ? null : canvas.toDataURL('image/png')),
      };
    }

    return () => {
      canvas.removeEventListener('pointerdown', down);
      canvas.removeEventListener('pointermove', move);
      canvas.removeEventListener('pointerup', up);
      canvas.removeEventListener('pointercancel', up);
    };
  }, [handleRef, onChange]);

  return (
    <canvas
      ref={canvasRef}
      className={className ?? 'h-32 w-full touch-none rounded border border-zinc-500 bg-white'}
      aria-label="Unterschriftsfeld"
    />
  );
}
